import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Close} from "@mui/icons-material";
import {Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography,  useTheme} from "@mui/material";
import { useRecoilValue} from "recoil";
import {FormProvider, useForm} from "react-hook-form";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {
    ACTION_DETAIL_INITIAL_VALUE, EXPIRED_DEFAULT_COL_DEF, EXPIRED_SIDE_BAR,
    getExpiredPracticeDetailActionHistoryListColmnDef, GRID_LIST_PAGE_SIZE
} from "../../config";
import {ColDef, ColumnMovedEvent, ColumnVisibleEvent, GridReadyEvent, SideBarDef} from "ag-grid-community";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {practiceActionHistory} from "../../state";
import {FormDatePicker, FormTextField} from "../../common/form-fields-wrappers";
import {IActionDetailForm, IExpiredActionInsertionForm} from "../../models/models";

const DetailActionHistory = () => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const {globalData} = useContext(GlobalContext);

    const actionHistory = useRecoilValue(practiceActionHistory);
    const [actionDetailDialogStatus, setActionDetailDialogStatus] = useState(false);
    const [actionDetailDialog, setActionDetailDialog] = useState(null);

    const gridRef = useRef<AgGridReact>(null);
    const columnDefs = useMemo<ColDef[]>(() => {
        return getExpiredPracticeDetailActionHistoryListColmnDef(t, viewActionDetail);
    }, [t]);

    const defaultColDef = useMemo<ColDef>(() => EXPIRED_DEFAULT_COL_DEF, []);

    const columnStateStorageKey = useMemo(() => {
        return `__EXPIRED_PRACTICE_DETAIL_ACTION_HISTORY.tableColumsConfig:${globalData.user.username}`;
    }, [globalData.user.username]);

    const sideBar = useMemo<SideBarDef>(() => EXPIRED_SIDE_BAR, []);

    const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });

        event.api.sizeColumnsToFit();
    }, [columnStateStorageKey]);

    const onCloseActionDetailDialog = () => {
        setActionDetailDialogStatus(false);
        setActionDetailDialog(null);
    }

    function viewActionDetail(e: any, data: any) {
        setActionDetailDialogStatus(true);
        setActionDetailDialog(data);
    }

    return (
        <>
            <Typography variant="bodyLarge" component="div" sx={{mb: theme.spacing(1)}}>
                {t('actionHistory.label.actionHistoryInfo')}
            </Typography>

            <AgGridCustomTable
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={
                    {
                        defaultColDef: defaultColDef,
                        rowModelType: 'clientSide',
                        pagination: false
                    }
                }
                rowData={actionHistory}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
                onGridReady={onGridReady}
                serverSideInfiniteScroll
                paginationPageSize={GRID_LIST_PAGE_SIZE}
                cacheBlockSize={GRID_LIST_PAGE_SIZE}
                sideBar={sideBar}
                suppressDragLeaveHidesColumns
                suppressMultiSort
                enableCellTextSelection

                // loadingCellRenderer={loadingCellRenderer}
            />

            <ActionDetailDialog onClose={onCloseActionDetailDialog} detail={actionDetailDialog} open={actionDetailDialogStatus} />

        </>
    );
}

export default DetailActionHistory;

/**
 * Dialog per la visualizzazione del dettaglio della Action selezionata
 * @param props
 * @constructor
 */
const ActionDetailDialog =  (props: any) => {
    const {onClose = null, open, detail} = props;
    const {t} = useTranslation(['expiredManagement']);
    const theme = useTheme();

    const actionDetailForm = useForm<IActionDetailForm>({
        defaultValues: ACTION_DETAIL_INITIAL_VALUE
    })

    const handleClose = (event: React.SyntheticEvent) => {
        event.stopPropagation();

        if (!!onClose) {
            onClose();
        }
    };

    useEffect(() => {
        if (!!detail) {
            actionDetailForm.setValue('createdDatetime', !!detail?.createdDatetime ? dayjs(detail.createdDatetime) : null);
            actionDetailForm.setValue('executionDate', !!detail?.executionDate ? dayjs(detail.executionDate) : null);
            actionDetailForm.setValue('updatedDatetime', !!detail?.updatedDatetime ? dayjs(detail.updatedDatetime) : null);
            actionDetailForm.setValue('creationUser', detail?.creationUser || '');
            actionDetailForm.setValue('action', detail?.action);
            actionDetailForm.setValue('recoveryCompany', detail?.recoveryCompany?.name || '');
            actionDetailForm.setValue('body', detail?.body ?? (detail?.note || ''));
        }
    }, [detail]);

    return (
        <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'lg'} sx={{minWidth: "400px"}}>
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <Typography>
                    {t('actionHistory.label.actionDetailTitle')}
                </Typography>

                <IconButton onClick={handleClose} sx={{ml: theme.spacing(4), mr: "0px"}}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <FormProvider {...actionDetailForm}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDatePicker<IActionDetailForm>
                                name="createdDatetime"
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.creationDatetime')}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDatePicker<IActionDetailForm>
                                name="executionDate"
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.executionDate')}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDatePicker<IActionDetailForm>
                                name="updatedDatetime"
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.updatedDatetime')}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IActionDetailForm>
                                name='creationUser'
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.creationUser')}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IActionDetailForm>
                                name='action'
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.action')}
                                fullWidth
                                disabled
                                translationLabelValue="common.actions"
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IActionDetailForm>
                                name='recoveryCompany'
                                control={actionDetailForm.control}
                                label={t('actionHistory.form.recoveryCompany')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                       
                        <Grid item xs={12} sx={{display: "flex", alignItems: "center", pt: theme.spacing(1)}}>
                            <Stack direction={"column"} gap={theme.spacing(1)} sx={{width:'100%'}}>
                                <Typography>{t('actionHistory.form.body')}</Typography>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'primary.main'
                                    }}
                                >
                                    <CardContent>
                                        <FormTextField<IExpiredActionInsertionForm>
                                            name="body"
                                            disabled={true}
                                            autoFocus
                                            fullWidth
                                            multiline
                                            rows={5}
                                            InputProps={{
                                                disableUnderline: true
                                            }}
                                            FormHelperTextProps={{
                                                component: "div"
                                            } as any}
                                        />
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Grid>
                    </Grid>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
}